'use client';

import { logger } from '@magicschool/logger';
import { useBreakpoint } from '@magicschool/spellbook';
import { isPreview, isProduction } from '@magicschool/utils/nextjs/metadata';
import type { accessiBeConfig, accessiBeLanguage, triggerSizes, triggerXPositions, triggerYPositions } from 'declarations/accessiBe';
import Script from 'next/script';
import { useState } from 'react';
import { AccessiBeFab } from './AccessiBeFab';

/**
 * Creates the AccessiBe configuration object.
 * The hideTrigger property is determined by whether the custom launcher feature flag is enabled.
 * When the custom launcher is enabled, we hide the default trigger and show our custom FAB.
 * When disabled, we show the default AccessiBe trigger.
 */
function createAccessiBeConfig(): accessiBeConfig {
  return {
    statementLink: 'https://www.magicschool.ai/accessibility',
    footerHtml: '',
    hideMobile: process.env.NEXT_PUBLIC_ACCESSIBE_HIDE_MOBILE ? process.env.NEXT_PUBLIC_ACCESSIBE_HIDE_MOBILE === 'true' : false,
    hideTrigger: true, // hide default trigger in favor of custom FAB
    disableBgProcess: true,
    language: 'en' as accessiBeLanguage, // default to english for now
    position: 'right',
    leadColor: '#146ff8',
    triggerColor: '#146ff8',
    triggerRadius: '50%',
    triggerPositionX: (process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_POSITION_X as triggerXPositions) || 'right',
    triggerPositionY: (process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_POSITION_Y as triggerYPositions) || 'bottom',
    triggerIcon: 'people',
    triggerSize: (process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_BUTTON_SIZE as triggerSizes) || 'medium',
    triggerOffsetX: process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_HORIZONTAL_OFFSET
      ? Number.parseInt(process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_HORIZONTAL_OFFSET)
      : 20,
    triggerOffsetY: process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_VERTICAL_OFFSET
      ? Number.parseInt(process.env.NEXT_PUBLIC_ACCESSIBE_DESKTOP_VERTICAL_OFFSET)
      : 20,
    mobile: {
      triggerSize: (process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_BUTTON_SIZE as triggerSizes) || 'small',
      triggerPositionX: (process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_POSITION_X as triggerXPositions) || 'right',
      triggerPositionY: (process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_POSITION_Y as triggerYPositions) || 'bottom',
      triggerOffsetX: process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_HORIZONTAL_OFFSET
        ? Number.parseInt(process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_HORIZONTAL_OFFSET)
        : 10,
      triggerOffsetY: process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_VERTICAL_OFFSET
        ? Number.parseInt(process.env.NEXT_PUBLIC_ACCESSIBE_MOBILE_VERTICAL_OFFSET)
        : 10,
      triggerRadius: '50%',
    },
  };
}

/**
 * accessiBe accessibility widget client component.
 *
 * Conditionally loads the accessiBe accessibility widget script based on environment.
 * The widget is loaded in production and ALL preview environments (not just localhost).
 *
 * Note: This means accessiBe will attempt to load on all preview deployments. For proper
 * functionality and testing, use the `accessibe-preproduction` branch which has a valid
 * accessiBe license configured for its preview deployment.
 *
 * Includes a custom FAB (Floating Action Button) that replaces the default accessiBe trigger.
 * The custom FAB is styled as a tab popout on the right edge of the viewport.
 */
function AccessiBeClient() {
  const [isAccessiBeReady, setIsAccessiBeReady] = useState(false);
  const isMobileOrSmaller = useBreakpoint('sm', 'down');

  const config = createAccessiBeConfig();

  const hideCustomTriggerDesktop = process.env.NEXT_PUBLIC_ACCESSIBE_HIDE_TRIGGER
    ? process.env.NEXT_PUBLIC_ACCESSIBE_HIDE_TRIGGER === 'true'
    : false;

  const hideCustomTriggerMobile = process.env.NEXT_PUBLIC_ACCESSIBE_HIDE_TRIGGER_MOBILE
    ? process.env.NEXT_PUBLIC_ACCESSIBE_HIDE_TRIGGER_MOBILE === 'true'
    : false;

  const hideForCurrentViewport = isMobileOrSmaller ? hideCustomTriggerMobile : hideCustomTriggerDesktop;
  const shouldShowCustomTrigger = !hideForCurrentViewport;

  if (isProduction() || isPreview()) {
    return (
      <>
        <Script
          src="https://acsbapp.com/apps/app/dist/js/app.js"
          async
          onLoad={() => {
            try {
              window.acsbJS.init(config);
              setIsAccessiBeReady(true);
            } catch (error) {
              // TODO: Maybe notify the user of this error?
              logger.error('Failed to initialize accessiBe:', error);
            }
          }}
        />
        {shouldShowCustomTrigger && isAccessiBeReady && <AccessiBeFab />}
      </>
    );
  }

  // For non-production environments, show the custom FAB for testing if enabled
  return (
    <>
      <div
        style={{ display: 'none' }}
        id="accessiBe-widget"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: placeholder comment for script
        dangerouslySetInnerHTML={{
          __html: `<!-- <script src="https://acsbapp.com/apps/app/dist/js/app.js"></script> -->`,
        }}
      />
      {shouldShowCustomTrigger && (
        <AccessiBeFab
          onClick={() => {
            console.warn(
              'accessiBe is non-functional in development. It will only work in production or the accessibe-preproduction deploy preview branch.',
            );
          }}
        />
      )}
    </>
  );
}

export default AccessiBeClient;
